import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './Homepage.scss'
import CapabilityChat from '../../components/CapabilityChat/CapabilityChat'
import GitHubActivity from '../../components/GitHubActivity/GitHubActivity'
import SponsorDialog, { QuickContact } from '../../components/SponsorDialog/SponsorDialog'
import NeuralCanvas from '../../components/NeuralCanvas/NeuralCanvas'
import AnimatedCounter from '../../components/AnimatedCounter/AnimatedCounter'
import { OPEN_PALETTE_EVENT } from '../../components/CommandPalette/CommandPalette'
import useReveal from '../../hooks/useReveal'
import { capabilities, experience, experiments, storeApps, sponsorship, labLoop, labStatuses, platformGroups, platforms, profile, projects, rotatingRoles, stack, stats } from '../../data/portfolio'
import { prefersReducedMotion, scrollToId } from '../../utils/scroll'

const codeLines = [
  [['kw', 'from'], ['', ' '], ['mod', 'paradzai'], ['', ' '], ['kw', 'import'], ['', ' '], ['cls', 'Engineer']],
  [],
  [['var', 'pg'], ['', ' = '], ['cls', 'Engineer'], ['', '(']],
  [['', '    '], ['attr', 'role'], ['', '='], ['str', '"AI Software Engineer"'], ['', ',']],
  [['', '    '], ['attr', 'focus'], ['', '=['], ['str', '"LLMs"'], ['', ', '], ['str', '"data"'], ['', ', '], ['str', '"platforms"'], ['', '],']],
  [['', '    '], ['attr', 'based_in'], ['', '='], ['str', '"Harare, ZW"'], ['', ',']],
  [['', ')']],
  [],
  [['var', 'pg'], ['', '.'], ['fn', 'ship'], ['', '('], ['var', 'your_idea'], ['', ')']],
  [['cm', '# ✓ shipped to production']]
]
const lineLengths = codeLines.map((line) => line.reduce((n, [, text]) => n + text.length, 0))
const lineStarts = lineLengths.map((_, i) => lineLengths.slice(0, i).reduce((sum, len) => sum + len + 1, 0))
const totalChars = lineStarts[lineStarts.length - 1] + lineLengths[lineLengths.length - 1]

const Typewriter = ({ words }) => {
  const [index, setIndex] = useState(0)
  const [text, setText] = useState(prefersReducedMotion() ? words[0] : '')
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (prefersReducedMotion()) return undefined
    const word = words[index]
    let delay = deleting ? 35 : 70
    if (!deleting && text === word) delay = 1700
    if (deleting && text === '') delay = 300

    const timer = setTimeout(() => {
      if (!deleting && text === word) setDeleting(true)
      else if (deleting && text === '') { setDeleting(false); setIndex((i) => (i + 1) % words.length) }
      else setText(word.slice(0, text.length + (deleting ? -1 : 1)))
    }, delay)
    return () => clearTimeout(timer)
  }, [text, deleting, index, words])

  return <span className="typewriter" aria-live="off">{text}<span className="typewriter-caret" /></span>
}

const CodeWindow = () => {
  const [typed, setTyped] = useState(prefersReducedMotion() ? totalChars : 0)
  const cardRef = useRef(null)

  useEffect(() => {
    if (typed >= totalChars) return undefined
    const timer = setTimeout(() => setTyped((n) => n + 1), typed === 0 ? 700 : 24)
    return () => clearTimeout(timer)
  }, [typed])

  const onMove = (event) => {
    const card = cardRef.current
    if (!card || prefersReducedMotion()) return
    const rect = card.getBoundingClientRect()
    const x = (event.clientX - rect.left) / rect.width - 0.5
    const y = (event.clientY - rect.top) / rect.height - 0.5
    card.style.setProperty('--rx', `${(-y * 8).toFixed(2)}deg`)
    card.style.setProperty('--ry', `${(x * 10).toFixed(2)}deg`)
  }
  const onLeave = () => {
    cardRef.current?.style.setProperty('--rx', '0deg')
    cardRef.current?.style.setProperty('--ry', '0deg')
  }

  const done = typed >= totalChars

  return (
    <div className="code-stage" onMouseMove={onMove} onMouseLeave={onLeave}>
      <div className="code-window" ref={cardRef}>
        <div className="code-window__bar">
          <span className="dot dot-red" /><span className="dot dot-amber" /><span className="dot dot-green" />
          <span className="code-window__title">engineer.py</span>
        </div>
        <pre className="code-window__body" aria-label="Python snippet introducing Paradzai as an AI Software Engineer">
          {codeLines.map((line, lineIndex) => {
            const start = lineStarts[lineIndex]
            let budget = Math.min(Math.max(typed - start, 0), lineLengths[lineIndex])
            const hasCursor = !done && typed >= start && typed <= start + lineLengths[lineIndex]
            return (
              <div className="code-line" key={lineIndex} style={{ opacity: typed >= start ? 1 : 0 }}>
                <span className="code-ln">{lineIndex + 1}</span>
                <span>
                  {line.map(([kind, text], tokenIndex) => {
                    const shown = text.slice(0, budget)
                    budget -= shown.length
                    return shown ? <span key={tokenIndex} className={kind ? `tok-${kind}` : undefined}>{shown}</span> : null
                  })}
                  {hasCursor && <span className="code-cursor" />}
                </span>
              </div>
            )
          })}
        </pre>
        <div className="code-window__status">
          <span><i className="fas fa-code-branch" /> main</span>
          <span className={`status-live ${done ? 'is-done' : ''}`}>{done ? 'deployed' : 'running…'}</span>
        </div>
      </div>
      <span className="float-chip chip-a"><i className="fas fa-brain" /> LLMs</span>
      <span className="float-chip chip-b"><i className="fas fa-bolt" /> Real-time data</span>
      <span className="float-chip chip-c"><i className="fas fa-shield-halved" /> Secure by design</span>
    </div>
  )
}

const agentTrace = [
  ['input', '"Why did my top-up fail?"'],
  ['retrieve', 'billing_events · 3 matches'],
  ['reason', 'balance below threshold at 14:02'],
  ['act', 'refund issued · customer notified ✓']
]

// Tracks the pointer over a card so CSS can paint a spotlight at --mx/--my.
const trackSpotlight = (event) => {
  const card = event.currentTarget
  const rect = card.getBoundingClientRect()
  card.style.setProperty('--mx', `${event.clientX - rect.left}px`)
  card.style.setProperty('--my', `${event.clientY - rect.top}px`)
}

const Homepage = () => {
  const rootRef = useRef(null)
  const [activeJob, setActiveJob] = useState(experience[0].id)
  const [copied, setCopied] = useState(false)
  const [sponsorDialog, setSponsorDialog] = useState(null)
  useReveal(rootRef)

  const job = experience.find((item) => item.id === activeJob)

  const copyEmail = () => {
    navigator.clipboard?.writeText(profile.email)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const onJobKey = (event, index) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
    event.preventDefault()
    const step = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1
    const next = experience[(index + step + experience.length) % experience.length]
    setActiveJob(next.id)
    document.getElementById(`job-tab-${next.id}`)?.focus()
  }

  return (
    <main className="pf" ref={rootRef}>
      {/* ───────── Hero ───────── */}
      <section id="home" className="pf-hero">
        <NeuralCanvas />
        <div className="pf-hero__glow glow-violet" aria-hidden="true" />
        <div className="pf-hero__glow glow-cyan" aria-hidden="true" />
        <div className="pf-hero__grid" aria-hidden="true" />

        <div className="pf-container pf-hero__layout">
          <div className="pf-hero__copy">
            <a className="pf-badge" href="#experience" onClick={(event) => { event.preventDefault(); scrollToId('experience') }}>
              <span className="pf-badge__pulse" />
              <span className="pf-badge__long">Now: AI Software Engineer at</span><span className="pf-badge__short">Now at</span> <strong>Econet AI</strong>
              <i className="fas fa-arrow-right" />
            </a>
            <h1 className="pf-hero__title">
              Engineering <span className="pf-gradient-text">intelligent systems</span> that actually ship.
            </h1>
            <p className="pf-hero__lead">
              I&apos;m <strong>Paradzai Gwira</strong>, a formally trained autodidact who builds <Typewriter words={rotatingRoles} />
              <br />for telecom, fintech and enterprise teams. I learn fast, prototype like a research lab and ship to production.
            </p>
            <div className="pf-hero__actions">
              <a data-magnetic className="pf-btn pf-btn--primary" href="#work" onClick={(event) => { event.preventDefault(); scrollToId('work') }}>
                View my work <i className="fas fa-arrow-right" />
              </a>
              <a data-magnetic className="pf-btn pf-btn--ghost" href="#ask" onClick={(event) => { event.preventDefault(); scrollToId('ask') }}>
                <i className="fas fa-wand-magic-sparkles" /> Ask my AI
              </a>
              <Link data-magnetic className="pf-btn pf-btn--ghost" to={profile.cvPage}>
                <i className="fas fa-id-card" /> View CV
              </Link>
            </div>
            <div className="pf-hero__meta">
              <div className="pf-socials">
                <a href={profile.github} target="_blank" rel="noreferrer" aria-label="GitHub"><i className="fa-brands fa-github" /></a>
                <a href={profile.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn"><i className="fa-brands fa-linkedin-in" /></a>
                <a href={profile.whatsapp} target="_blank" rel="noreferrer" aria-label="WhatsApp"><i className="fa-brands fa-whatsapp" /></a>
                <a href={`mailto:${profile.email}`} aria-label="Email"><i className="fas fa-envelope" /></a>
              </div>
              <button type="button" className="pf-kbd-hint" onClick={() => window.dispatchEvent(new Event(OPEN_PALETTE_EVENT))}>
                Press <kbd>⌘</kbd><kbd>K</kbd> to jump anywhere
              </button>
            </div>
          </div>
          <CodeWindow />
        </div>

        <button type="button" className="pf-scroll-cue" onClick={() => scrollToId('capabilities')} aria-label="Scroll to capabilities">
          <span className="pf-scroll-cue__mouse"><span /></span>
        </button>
      </section>

      {/* ───────── Stats ───────── */}
      <section className="pf-stats" aria-label="Career highlights">
        <div className="pf-container pf-stats__grid">
          {stats.map((stat, index) => (
            <div className="pf-stat" key={stat.label} data-reveal style={{ '--delay': `${index * 90}ms` }}>
              <AnimatedCounter end={stat.value} suffix={stat.suffix} duration={1600} />
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ───────── Capabilities ───────── */}
      <section id="capabilities" className="pf-section">
        <div className="pf-container">
          <header className="pf-heading" data-reveal>
            <p className="pf-eyebrow"><span>01</span> Capabilities</p>
            <h2>From model to <span className="pf-gradient-text">production</span>, end to end.</h2>
            <p>AI is only useful when it&apos;s wired into reliable systems. I work across the whole path: data in, intelligence in the middle, dependable software out.</p>
          </header>
          <div className="pf-bento">
            {capabilities.map((item, index) => (
              <article
                key={item.title}
                className={`pf-card pf-spot ${item.featured ? 'pf-card--featured' : ''}`}
                onMouseMove={trackSpotlight}
                data-reveal
                style={{ '--delay': `${index * 70}ms` }}
              >
                <div className="pf-card__icon"><i className={`fas ${item.icon}`} /></div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
                <div className="pf-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                {item.featured && (
                  <div className="pf-trace" aria-hidden="true">
                    <div className="pf-trace__bar"><span>agent.trace</span><span className="pf-trace__live">live</span></div>
                    {agentTrace.map(([step, text], stepIndex) => (
                      <div className="pf-trace__row" key={step} style={{ '--i': stepIndex }}>
                        <span className={`pf-trace__step step-${step}`}>{step}</span>
                        <span className="pf-trace__text">{text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>

          {platformGroups.map((group) => (
            <div className="pf-platforms" key={group.id}>
              <p className="pf-platforms__label" data-reveal>{group.label}</p>
              <div className={`pf-platforms__grid group-${group.id}`}>
                {platforms.filter((platform) => platform.group === group.id).map((platform, index) => (
                  <article
                    key={platform.name}
                    className="pf-platform pf-spot"
                    onMouseMove={trackSpotlight}
                    data-reveal
                    style={{ '--delay': `${index * 80}ms` }}
                  >
                    <div className="pf-platform__logo">
                      {platform.logo
                        ? <img src={platform.logo} alt={`${platform.name} logo`} loading="lazy" />
                        : (
                          <span className="pf-platform__mark">
                            {platform.markImg ? <img src={platform.markImg} alt="" /> : <i className={platform.icon} aria-hidden="true" />}
                            {platform.name}
                          </span>
                        )}
                    </div>
                    <p className="pf-platform__role">{platform.role}</p>
                    <h3>{platform.name}</h3>
                    <p className="pf-platform__text">{platform.text}</p>
                    <div className="pf-tags">{platform.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ───────── Experience ───────── */}
      <section id="experience" className="pf-section pf-section--alt">
        <div className="pf-container">
          <header className="pf-heading" data-reveal>
            <p className="pf-eyebrow"><span>02</span> Experience</p>
            <h2>Where I&apos;ve been <span className="pf-gradient-text">shipping</span>.</h2>
            <p>Real-world complexity: carrier networks, money movement and live data. Pick a role to see the details.</p>
          </header>

          <div className="pf-exp" data-reveal>
            <div className="pf-exp__tabs" role="tablist" aria-label="Employers" aria-orientation="vertical">
              {experience.map((item, index) => (
                <button
                  key={item.id}
                  id={`job-tab-${item.id}`}
                  type="button"
                  role="tab"
                  aria-selected={activeJob === item.id}
                  aria-controls="job-panel"
                  tabIndex={activeJob === item.id ? 0 : -1}
                  className={`pf-exp__tab ${activeJob === item.id ? 'is-active' : ''}`}
                  onClick={() => setActiveJob(item.id)}
                  onKeyDown={(event) => onJobKey(event, index)}
                >
                  <span className="pf-exp__logo"><img src={item.logo} alt="" /></span>
                  <span className="pf-exp__tab-text">
                    <strong>{item.company}</strong>
                    <small>{item.period}</small>
                  </span>
                  {item.current && <span className="pf-exp__now">Now</span>}
                </button>
              ))}
            </div>

            <div className="pf-exp__panel pf-spot" id="job-panel" role="tabpanel" aria-labelledby={`job-tab-${job.id}`} key={job.id} onMouseMove={trackSpotlight}>
              <div className="pf-exp__meta">
                <span><i className="far fa-calendar" /> {job.period}</span>
                <span><i className="fas fa-location-dot" /> {job.location}</span>
              </div>
              <h3>{job.role} <span>@ {job.company}</span></h3>
              <p className="pf-exp__summary">{job.summary}</p>
              <ul className="pf-exp__list">
                {job.highlights.map((point, index) => <li key={point} style={{ '--delay': `${index * 70}ms` }}>{point}</li>)}
              </ul>
              <div className="pf-tags">{job.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── Lab ───────── */}
      <section id="lab" className="pf-section pf-lab">
        <div className="pf-container">
          <header className="pf-heading" data-reveal>
            <p className="pf-eyebrow"><span>03</span> The Lab</p>
            <h2>Learn. Explore. Build. <span className="pf-gradient-text">Deliver.</span></h2>
            <p>I&apos;m a formally trained autodidact: university gave me the foundations (algorithms, discrete mathematics, system design), and every modern framework, language and tool I ship with, I taught myself. Everything goes through the same loop, and the bench is never empty.</p>
          </header>

          <div className="pf-loop" data-reveal>
            <div className="pf-loop__track" aria-hidden="true"><span className="pf-loop__pulse" /></div>
            {labLoop.map((step, index) => (
              <div className="pf-loop__step" key={step.id} style={{ '--i': index }}>
                <span className="pf-loop__icon"><i className={`fas ${step.icon}`} /></span>
                <span className="pf-loop__num">0{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            ))}
          </div>

          <div className="pf-bench">
            <div className="pf-bench__head" data-reveal>
              <span><i className="fas fa-flask-vial" /> lab-notebook.md</span>
              <span className="pf-bench__count">{experiments.length} entries</span>
            </div>
            <div className="pf-bench__grid">
              {experiments.map((item, index) => {
                const status = labStatuses[item.status]
                const internal = item.href && item.href.startsWith('#')
                const Tag = item.href ? 'a' : 'article'
                const linkProps = item.href
                  ? internal
                    ? { href: item.href, onClick: (event) => { event.preventDefault(); scrollToId(item.href.slice(1)) } }
                    : { href: item.href, target: '_blank', rel: 'noreferrer' }
                  : {}
                return (
                  <Tag
                    key={item.code}
                    className={`pf-exp-card pf-spot lab-${item.status}`}
                    onMouseMove={trackSpotlight}
                    data-reveal
                    style={{ '--delay': `${(index % 3) * 80}ms` }}
                    {...linkProps}
                  >
                    <div className="pf-exp-card__top">
                      <span className="pf-exp-card__code">{item.code}</span>
                      <span className="pf-status"><i className={`fas ${status.icon}`} /> {status.label}</span>
                    </div>
                    <h3>{item.title}{item.href && <i className={`fas ${internal ? 'fa-arrow-down' : 'fa-arrow-up-right-from-square'} pf-exp-card__out`} />}</h3>
                    <p>{item.text}</p>
                    <div className="pf-tags">{item.stack.map((tag) => <span key={tag}>{tag}</span>)}</div>
                  </Tag>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ───────── Stack marquee ───────── */}
      <section className="pf-marquee" aria-label="Technology stack">
        {[stack, [...stack].reverse()].map((row, rowIndex) => (
          <div className={`pf-marquee__row ${rowIndex ? 'is-reverse' : ''}`} key={rowIndex}>
            <div className="pf-marquee__track">
              {[...row, ...row].map((tech, index) => (
                <span className="pf-marquee__item" key={`${tech.name}-${index}`} aria-hidden={index >= row.length}>
                  <i className={tech.icon} /> {tech.name}
                </span>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* ───────── Work ───────── */}
      <section id="work" className="pf-section">
        <div className="pf-container">
          <header className="pf-heading" data-reveal>
            <p className="pf-eyebrow"><span>04</span> Selected work</p>
            <h2>Built for the <span className="pf-gradient-text">messy middle</span>.</h2>
            <p>Systems where data, money and conversations meet real users, from streaming pipelines to apps in the store.</p>
          </header>
          <div className="pf-apps" data-reveal>
            <span className="pf-apps__label"><i className="fas fa-store" /> Live in public stores</span>
            <ul>
              {storeApps.map((app, index) => <li key={app} style={{ '--i': index }}>{app}</li>)}
            </ul>
            <span className="pf-apps__platforms">
              <i className="fa-brands fa-android" title="Android" /><i className="fa-brands fa-apple" title="iOS" /><i className="fas fa-mobile-screen" title="Huawei AppGallery" /><i className="fas fa-globe" title="Web (PWA)" />
            </span>
          </div>
          <div className="pf-work">
            {projects.map((project, index) => (
              <article
                key={project.title}
                className={`pf-project pf-spot ${project.wide ? 'pf-project--wide' : ''}`}
                onMouseMove={trackSpotlight}
                data-reveal
                style={{ '--delay': `${(index % 3) * 80}ms` }}
              >
                <p className="pf-project__kicker">{project.kicker}</p>
                <h3>{project.title}</h3>
                <p className="pf-project__text">{project.text}</p>
                {project.flow && (
                  <div className="pf-flow" aria-hidden="true">
                    {project.flow.map((step, stepIndex) => (
                      <React.Fragment key={step}>
                        {stepIndex > 0 && <span className="pf-flow__edge"><span className="pf-flow__packet" style={{ animationDelay: `${stepIndex * 0.35}s` }} /></span>}
                        <span className="pf-flow__node">{step}</span>
                      </React.Fragment>
                    ))}
                  </div>
                )}
                <div className="pf-project__foot">
                  <div className="pf-tags">{project.stack.map((tag) => <span key={tag}>{tag}</span>)}</div>
                  {project.links && (
                    <div className="pf-project__links">
                      {project.links.map((link) => {
                        const internal = link.href.startsWith('#')
                        return (
                          <a
                            key={link.label}
                            href={link.href}
                            target={internal ? undefined : '_blank'}
                            rel={internal ? undefined : 'noreferrer'}
                            onClick={internal ? (event) => { event.preventDefault(); scrollToId(link.href.slice(1)) } : undefined}
                          >
                            {link.label} <i className={`fas ${internal ? 'fa-arrow-down' : 'fa-arrow-up-right-from-square'}`} />
                          </a>
                        )
                      })}
                    </div>
                  )}
                </div>
              </article>
            ))}
            <a className="pf-project pf-project--cta pf-spot" href={profile.github} target="_blank" rel="noreferrer" onMouseMove={trackSpotlight} data-reveal style={{ '--delay': '160ms' }}>
              <i className="fa-brands fa-github pf-project__gh" />
              <h3>More experiments on GitHub</h3>
              <p className="pf-project__text">Side projects, prototypes and open-source tinkering.</p>
              <span className="pf-project__cta-link">github.com/PRedemption <i className="fas fa-arrow-right" /></span>
            </a>
          </div>
        </div>
      </section>

      {/* ───────── GitHub ───────── */}
      <GitHubActivity />

      {/* ───────── Sponsor & mentor ───────── */}
      <section id="sponsor" className="pf-section pf-sponsor">
        <div className="pf-container">
          <header className="pf-heading" data-reveal>
            <p className="pf-eyebrow"><span>06</span> Sponsor &amp; mentor</p>
            <h2>Back the lab. Grow the <span className="pf-gradient-text">next builders</span>.</h2>
            <p>I want to turn my lab into a launchpad: stronger open-source projects, and a mentorship program for young, energetic engineers who are hungry to learn. Sponsorship makes both sustainable.</p>
          </header>

          <div className="pf-pillars">
            {sponsorship.pillars.map((pillar, index) => (
              <div className="pf-pillar" key={pillar.title} data-reveal style={{ '--delay': `${index * 70}ms` }}>
                <span className="pf-pillar__icon"><i className={`fas ${pillar.icon}`} /></span>
                <h3>{pillar.title}</h3>
                <p>{pillar.text}</p>
              </div>
            ))}
          </div>

          <div className="pf-tiers">
            {sponsorship.tiers.map((tier, index) => {
              return (
                <article key={tier.id} className={`pf-tier pf-spot ${tier.featured ? 'is-featured' : ''}`} onMouseMove={trackSpotlight} data-reveal style={{ '--delay': `${index * 90}ms` }}>
                  {tier.featured && <span className="pf-tier__flag">Most impact</span>}
                  <h3>{tier.name}</h3>
                  <p className="pf-tier__cadence">{tier.cadence}</p>
                  <ul>{tier.perks.map((perk) => <li key={perk}><i className="fas fa-check" /> {perk}</li>)}</ul>
                  <button type="button" className={`pf-btn ${tier.featured ? 'pf-btn--primary' : 'pf-btn--ghost'}`} onClick={() => setSponsorDialog({ kind: 'sponsor', tier })} data-magnetic>
                    <i className="fas fa-hand-holding-heart" /> {tier.cta}
                  </button>
                  <p className="pf-tier__or">or talk to me directly</p>
                  <QuickContact topic={`Sponsorship: ${tier.name}`} compact />
                </article>
              )
            })}
          </div>

          <div className="pf-sponsor-foot">
            <div className="pf-wall" data-reveal>
              <p className="pf-wall__label">Sponsors wall</p>
              <div className="pf-wall__slots" aria-hidden="true">{[0, 1, 2, 3, 4].map((slot) => <span key={slot} style={{ '--i': slot }} />)}</div>
              <p className="pf-wall__note">No sponsors yet. <strong>Your name could be the first one here.</strong></p>
            </div>
            <div className="pf-mentee" data-reveal style={{ '--delay': '120ms' }}>
              <i className="fas fa-seedling" />
              <div>
                <h3>Want to be mentored?</h3>
                <p>If you&apos;re early in your career and willing to learn, tell me what you want to build.</p>
              </div>
              <button type="button" className="pf-btn pf-btn--ghost" onClick={() => setSponsorDialog({ kind: 'mentee' })}>
                Apply <i className="fas fa-arrow-right" />
              </button>
            </div>
          </div>

          <div className="pf-talk" data-reveal>
            <div>
              <h3>Prefer to talk it through first?</h3>
              <p>Sponsors, partners and future mentees: pick whatever channel suits you. I reply personally.</p>
            </div>
            <QuickContact topic="Sponsorship and mentorship" />
            {sponsorship.githubSponsors && (
              <a className="pf-talk__gh" href={sponsorship.githubSponsors} target="_blank" rel="noreferrer"><i className="fa-brands fa-github" /> Sponsor on GitHub</a>
            )}
          </div>
        </div>
      </section>

      <SponsorDialog
        open={Boolean(sponsorDialog)}
        kind={sponsorDialog?.kind}
        tier={sponsorDialog?.tier}
        onClose={() => setSponsorDialog(null)}
      />

      {/* ───────── Ask AI ───────── */}
      <CapabilityChat />

      {/* ───────── Contact ───────── */}
      <section id="contact" className="pf-section pf-contact">
        <div className="pf-container">
          <div className="pf-contact__panel" data-reveal>
            <div className="pf-contact__orb" aria-hidden="true" />
            <p className="pf-eyebrow"><span>08</span> Contact</p>
            <h2>Have a hard problem?<br /><span className="pf-gradient-text">Let&apos;s ship it.</span></h2>
            <p>AI automation, data platforms or production backends: tell me what you&apos;re trying to change and let&apos;s figure out the fastest path to production.</p>
            <div className="pf-contact__actions">
              <a data-magnetic className="pf-btn pf-btn--primary pf-btn--lg" href={`mailto:${profile.email}`}>
                <i className="fas fa-paper-plane" /> Start a conversation
              </a>
              <button type="button" className={`pf-copy ${copied ? 'is-copied' : ''}`} onClick={copyEmail}>
                <span>{profile.email}</span>
                <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} />
                <span className="pf-copy__tip">{copied ? 'Copied!' : 'Copy'}</span>
              </button>
            </div>
            <div className="pf-contact__alt">
              <a href={profile.whatsapp} target="_blank" rel="noreferrer"><i className="fa-brands fa-whatsapp" /> WhatsApp</a>
              <a href={profile.linkedin} target="_blank" rel="noreferrer"><i className="fa-brands fa-linkedin-in" /> LinkedIn</a>
              <a href={profile.github} target="_blank" rel="noreferrer"><i className="fa-brands fa-github" /> GitHub</a>
              <span><i className="fas fa-location-dot" /> {profile.location}</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

export default Homepage
